import { useEffect, useMemo, useRef } from 'react';
import { tipImages } from '../../brush/library';
import { BRUSH_PRESET_CATEGORIES } from '../../brush/presets';
import { coverageToImageData, renderStrokePreview, renderTipThumbnail } from '../../brush/preview';
import {
  dynamicsFromOptions,
  presetFromOptions,
  tipFromOptions,
  type BrushPreset,
  type BrushPresetCategory,
  type BrushTip,
  type PaintOptions,
} from '../../brush/types';
import { createId } from '../../core/ids';
import { isPaintTool, type PaintToolId } from '../../core/types';
import {
  defineBrushFromLayer,
  exportBrushPresets,
  importBrushFile,
} from '../../state/brushActions';
import { getPaintOptions, useAppStore } from '../../state/store';
import { BLEND_MODE_GROUPS, BLEND_MODE_LABELS } from '../../core/blendModes';
import type { BlendMode } from '../../core/types';
import { Checkbox, Select, SliderRow } from '../components/controls';
import { Panel } from './Panel';

const BLEND_MODE_OPTIONS: { value: BlendMode; label: string }[] = BLEND_MODE_GROUPS.flat().map(
  (mode) => ({ value: mode, label: BLEND_MODE_LABELS[mode] }),
);

const TRANSFER_TOOLS = new Set<PaintToolId>(['brush', 'pencil', 'eraser', 'clone']);

const THUMBNAIL = 34;
const PREVIEW_WIDTH = 232;
const PREVIEW_HEIGHT = 64;
const INK = { r: 0.91, g: 0.91, b: 0.93 };

function CoverageCanvas({ image, className }: { image: ImageData; className: string }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) return;
    canvas.width = image.width;
    canvas.height = image.height;
    context.putImageData(image, 0, 0);
  }, [image]);
  return <canvas className={className} ref={ref} />;
}

function TipThumbnail({ tip, className }: { tip: BrushTip; className: string }) {
  const image = useMemo(() => coverageToImageData(renderTipThumbnail(tip, THUMBNAIL), INK), [tip]);
  return <CoverageCanvas image={image} className={className} />;
}

function PresetButton({
  preset,
  active,
  onSelect,
  onRemove,
}: {
  preset: BrushPreset;
  active: boolean;
  onSelect: () => void;
  onRemove: (() => void) | null;
}) {
  return (
    <div className={`brush-preset${active ? ' brush-preset--active' : ''}`}>
      <button
        type="button"
        className="brush-preset__button"
        title={`${preset.name} — ${Math.round(preset.size)} px`}
        onClick={onSelect}
      >
        <TipThumbnail tip={preset.tip} className="brush-preset__canvas" />
        <span className="brush-preset__size">{Math.round(preset.size)}</span>
      </button>
      {onRemove && (
        <button
          type="button"
          className="brush-preset__remove"
          title="Remove this preset"
          onClick={onRemove}
        >
          ×
        </button>
      )}
    </div>
  );
}

function StrokePreview({ options }: { options: PaintOptions }) {
  const image = useMemo(() => {
    const brush = {
      size: options.size,
      tip: tipFromOptions(options),
      dynamics: dynamicsFromOptions(options),
      opacity: options.opacity,
      flow: options.flow,
    };
    return coverageToImageData(renderStrokePreview(brush, PREVIEW_WIDTH, PREVIEW_HEIGHT), INK);
  }, [options]);
  return <CoverageCanvas image={image} className="brush-preview" />;
}

function pickBrushFile(): void {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = '.abr';
  input.onchange = () => {
    const file = input.files?.[0];
    if (file) void importBrushFile(file);
  };
  input.click();
}

/** A sampled tip shown as itself, with no hardness or squeeze applied. */
function plainTip(imageId: string): BrushTip {
  return { kind: 'pattern', hardness: 1, angle: 0, roundness: 1, spacing: 0.25, imageId };
}

function groupedBrushPresets(
  presets: BrushPreset[],
): { category: string; items: BrushPreset[] }[] {
  const byCategory = new Map<string, BrushPreset[]>();
  for (const category of BRUSH_PRESET_CATEGORIES) byCategory.set(category, []);
  byCategory.set('Custom', []);

  for (const preset of presets) {
    const key: BrushPresetCategory | 'Custom' = preset.category ?? 'Custom';
    const list = byCategory.get(key) ?? byCategory.get('Custom')!;
    list.push(preset);
  }

  return [...byCategory.entries()]
    .filter(([, items]) => items.length > 0)
    .map(([category, items]) => ({ category, items }));
}

export function BrushPanel() {
  const tool = useAppStore((state) => state.tool);
  const options = useAppStore((state) => getPaintOptions(state));
  const presets = useAppStore((state) => state.brushPresets);
  const patch = useAppStore((state) => state.patchPaintOptions);
  const applyBrushPreset = useAppStore((state) => state.applyBrushPreset);
  const addBrushPresets = useAppStore((state) => state.addBrushPresets);
  const removeBrushPreset = useAppStore((state) => state.removeBrushPreset);

  // Non-paint tools still show the Brush tool's settings, so the panel is never
  // empty and editing it prepares the brush for when it is picked again.
  const target: PaintToolId = isPaintTool(tool) ? tool : 'brush';
  const percent = (value: number): number => Math.round(value * 100);
  const slider = (
    label: string,
    value: number,
    max: number,
    apply: (fraction: number) => Partial<PaintOptions>,
  ) => (
    <SliderRow
      label={label}
      value={percent(value)}
      min={0}
      max={max}
      onChange={(next) => patch(target, apply(next / 100))}
    />
  );

  return (
    <Panel
      id="brush"
      title="Brush"
      actions={
        <>
          <button
            type="button"
            className="button button--small"
            onClick={() =>
              addBrushPresets([
                presetFromOptions(createId('preset'), `Brush ${Math.round(options.size)}`, options),
              ])
            }
          >
            New Preset
          </button>
          <button type="button" className="button button--small" onClick={pickBrushFile}>
            Load .ABR
          </button>
          <button
            type="button"
            className="button button--small"
            onClick={() => exportBrushPresets(presets)}
          >
            Save .ABR
          </button>
          <button type="button" className="button button--small" onClick={defineBrushFromLayer}>
            From Layer
          </button>
        </>
      }
    >
      <div className="panel__body">
        {groupedBrushPresets(presets).map(({ category, items }) => (
          <div key={category} className="brush-gallery-group">
            <div className="panel__section-title">{category}</div>
            <div className="brush-gallery">
              {items.map((preset) => (
                <PresetButton
                  key={preset.id}
                  preset={preset}
                  active={preset.id === options.presetId}
                  onSelect={() => applyBrushPreset(target, preset)}
                  onRemove={
                    preset.id.startsWith('preset.') ? null : () => removeBrushPreset(preset.id)
                  }
                />
              ))}
            </div>
          </div>
        ))}

        <div className="panel__section">
          <div className="panel__section-title">Tip Shape</div>
          <div className="brush-tips">
            <button
              type="button"
              className={`brush-tip${options.tipImageId === null ? ' brush-tip--active' : ''}`}
              title="Circular tip, generated from the hardness"
              onClick={() => patch(target, { tipImageId: null })}
            >
              <TipThumbnail
                tip={{ ...tipFromOptions(options), kind: 'circular', imageId: null }}
                className="brush-preset__canvas"
              />
            </button>
            {tipImages().map((tip) => (
              <button
                key={tip.id}
                type="button"
                className={`brush-tip${options.tipImageId === tip.id ? ' brush-tip--active' : ''}`}
                title={tip.name}
                onClick={() => patch(target, { tipImageId: tip.id })}
              >
                <TipThumbnail tip={plainTip(tip.id)} className="brush-preset__canvas" />
              </button>
            ))}
          </div>

          <SliderRow
            label="Size"
            value={Math.round(options.size)}
            min={1}
            max={1000}
            onChange={(size) => patch(target, { size })}
          />
          {options.tipImageId === null &&
            slider('Hardness', options.hardness, 100, (hardness) => ({ hardness }))}
          <SliderRow
            label="Angle"
            value={Math.round(options.angle)}
            min={-180}
            max={180}
            onChange={(angle) => patch(target, { angle })}
          />
          <SliderRow
            label="Roundness"
            value={percent(options.roundness)}
            min={5}
            max={100}
            onChange={(value) => patch(target, { roundness: value / 100 })}
          />
          <SliderRow
            label="Spacing"
            value={percent(options.spacing)}
            min={1}
            max={300}
            onChange={(value) => patch(target, { spacing: value / 100 })}
          />
          <SliderRow
            label="Smoothing"
            value={percent(options.smoothing)}
            min={0}
            max={90}
            onChange={(value) => patch(target, { smoothing: value / 100 })}
          />
        </div>

        {TRANSFER_TOOLS.has(target) && (
          <div className="panel__section">
            <div className="panel__section-title">Transfer</div>
            {slider('Opacity', options.opacity, 100, (opacity) => ({
              opacity: Math.max(0.01, opacity),
            }))}
            {slider('Flow', options.flow, 100, (flow) => ({ flow: Math.max(0.01, flow) }))}
            {target !== 'eraser' && (
              <div className="field" style={{ marginBottom: 8 }}>
                <span className="field__label" style={{ width: 72 }}>
                  Mode
                </span>
                <Select
                  value={options.blendMode}
                  options={BLEND_MODE_OPTIONS}
                  onChange={(blendMode) => patch(target, { blendMode })}
                />
              </div>
            )}
            <Checkbox
              label="Pressure → size"
              checked={options.pressureSize}
              onChange={(pressureSize) => patch(target, { pressureSize })}
            />
            {target !== 'clone' && (
              <Checkbox
                label="Pressure → opacity"
                checked={options.pressureOpacity}
                onChange={(pressureOpacity) => patch(target, { pressureOpacity })}
              />
            )}
          </div>
        )}

        {!TRANSFER_TOOLS.has(target) && (
          <div className="panel__section">
            <div className="panel__section-title">Stroke</div>
            <Checkbox
              label="Pressure → size"
              checked={options.pressureSize}
              onChange={(pressureSize) => patch(target, { pressureSize })}
            />
          </div>
        )}

        <div className="panel__section">
          <div className="panel__section-title">Dynamics</div>
          {slider('Size Jitter', options.sizeJitter, 100, (sizeJitter) => ({ sizeJitter }))}
          {slider('Angle Jitter', options.angleJitter, 100, (angleJitter) => ({ angleJitter }))}
          {slider('Roundness Jitter', options.roundnessJitter, 100, (roundnessJitter) => ({
            roundnessJitter,
          }))}
          {slider('Scatter', options.scatter, 200, (scatter) => ({ scatter }))}
          {slider('Flow Jitter', options.flowJitter, 100, (flowJitter) => ({ flowJitter }))}
          <label className="checkbox">
            <input
              type="checkbox"
              checked={options.followDirection}
              onChange={(event) => patch(target, { followDirection: event.target.checked })}
            />
            Follow stroke direction
          </label>
        </div>

        <StrokePreview options={options} />
      </div>
    </Panel>
  );
}
